/** Una tarjeta de evidencia: pestañas Gráfico | Tabla sobre el MISMO dato, y el pie de procedencia.
 *
 * Las pestañas son de la referencia del cliente (HPE AI Agent): las dos vistas del mismo dato
 * tienen el mismo peso y la tarjeta ocupa el alto de UNA, no de las dos. Antes el gráfico estaba
 * siempre visible y la tabla escondida detrás de un «ver tabla», y una tarjeta medía 619 px.
 *
 * El pie NO es decoración y no se puede perder: fuente con su owner, sistema de origen, corte de
 * datos, nivel epistémico, confianza y la marca de sintético. Es lo que exige `verificar()` en
 * Python, dicho en la pantalla.
 */
import { useState } from "react";
import type { Idioma, Tarjeta } from "../lib/tipos";
import { bi, t } from "../lib/i18n";
import { GraficoBarras } from "../graficos/Barras";
import { GraficoBarrasApiladas } from "../graficos/BarrasApiladas";
import { GraficoDispersion } from "../graficos/Dispersion";
import { GraficoHeatmap } from "../graficos/Heatmap";
import { GraficoRadar } from "../graficos/Radar";
import { GraficoSerie } from "../graficos/Serie";
import { Tabla } from "./Tabla";
import { celda, cifra as fmtCifra } from "../lib/formato";
import { ChipProcedencia } from "./ChipProcedencia";
import { VisorDocumento, type Documento } from "./VisorDocumento";

export function TarjetaEvidencia({ tarjeta, idioma, n, resaltada }: {
  tarjeta: Tarjeta; idioma: Idioma; n?: number; resaltada?: boolean;
}) {
  const txt = t(idioma);
  const g = tarjeta.grafico;
  const hayGrafico = !!g;
  const hayTabla = tarjeta.tabla.length > 0;
  const [vista, setVista] = useState<"grafico" | "tabla">(hayGrafico ? "grafico" : "tabla");

  // Los documentos que esta tarjeta puede abrir, y si el usuario pidió VERLOS. La ficha dice lo
  // que el producto DEBE medir y el informe de planta lo que MIDIÓ; son dos documentos y dos
  // banderas. Las pone el modelo al leer la intención; sin ellas la tarjeta solo ofrece enlaces.
  const lista = (clave: string) =>
    Array.isArray(tarjeta.extra?.[clave]) ? (tarjeta.extra[clave] as string[]) : [];
  const fichas = lista("fichas_pdf");
  const informes = lista("informes_pdf");
  const verFicha = fichas.length > 0 && tarjeta.extra?.mostrar_ficha === true;
  const verInforme = informes.length > 0 && tarjeta.extra?.mostrar_informe === true;
  const docs: Documento[] = [
    ...(verFicha ? fichas.map((sku) => ({ tipo: "ficha" as const, sku })) : []),
    ...(verInforme ? informes.map((sku) => ({ tipo: "informe" as const, sku })) : []),
  ];
  // El nombre comercial lo manda el motor (`nombres_pdf`), porque solo las tablas ANCHAS traen
  // una columna por artículo de la que deducirlo: la de capacidad es larga y sin esto las
  // pestañas dirían «48838004». Los encabezados se conservan como respaldo.
  const deEncabezados = Object.fromEntries(
    tarjeta.encabezados
      .filter((e) => fichas.includes(e.clave) || informes.includes(e.clave))
      .map((e) => [e.clave, bi(e, idioma)]),
  ) as Record<string, string>;
  const nombres: Record<string, string> = {
    ...deEncabezados,
    ...((tarjeta.extra?.nombres_pdf as Record<string, string>) ?? {}),
  };

  return (
    <section className={`tarjeta${resaltada ? " resaltada" : ""}`} id={`tarjeta-${tarjeta.id}`}
             aria-label={bi(tarjeta.titulo, idioma)}>
      <header className="tarjeta-cabeza">
        {n !== undefined && <span className="tarjeta-n" aria-hidden="true">{n}</span>}
        <h4>{bi(tarjeta.titulo, idioma)}</h4>
        <span className={`chip nivel-${tarjeta.nivel}`} title={bi(tarjeta.limite_nivel, idioma)}>
          {/* `derivado` es el único nivel que avisa en vez de describir, así que lleva signo
              además de color: el ámbar solo no llega a quien no lo distingue del azul. Va
              `aria-hidden` porque la palabra que sigue ya dice lo mismo. */}
          {tarjeta.nivel === "derivado" && <span className="signo-aviso" aria-hidden="true">⚠</span>}
          {txt.niveles[tarjeta.nivel]}
        </span>
      </header>

      {tarjeta.limite && <p className="limite">{bi(tarjeta.limite, idioma)}</p>}

      {/* El único nivel cuyo límite se pinta VISIBLE en la tarjeta. Los otros lo llevan en el
          `title` del chip, que en táctil no existe — y para «describe, no explica causas» eso
          basta. Aquí no: una cifra derivada sin su aviso al lado se copia a una lámina y viaja
          sola. La salvedad completa está en la sección de Salvedades de la respuesta. */}
      {tarjeta.nivel === "derivado" && <p className="nota-derivada">{bi(tarjeta.limite_nivel, idioma)}</p>}

      {/* EL DOCUMENTO, arriba del todo. Cuando lo que se pidió fue verlo, va antes que el gráfico
          y la tabla: «quiere ver el pdf, no texto cuando pide que le muestre el spec sheet». */}
      {docs.length > 0 && <VisorDocumento docs={docs} nombres={nombres} idioma={idioma} />}

      {/* LA FICHA EN PDF. Lo pidió un usuario de Essity probando la app: «what I really would need
          is simply just opening the right .pdf fact sheet for my eyes». Un resumen correcto no
          sustituye una firma visual para quien conoce el documento de memoria.
          Abre en pestaña nueva —no reemplaza la conversación— y con `rel` por el `target`. */}
      {fichas.length > 0 && !verFicha && (
        <p className="fichas-pdf">
          {txt.abrirFicha}{" "}
          {fichas.map((sku, i) => (
            <span key={sku}>
              {i > 0 && " · "}
              <a href={`/api/ficha/${encodeURIComponent(sku)}`} target="_blank" rel="noopener noreferrer">{nombres[sku] || sku}</a>
            </span>
          ))}
        </p>
      )}

      {/* EL INFORME DE PLANTA, al pie y por lo mismo: lo que la planta MIDIÓ, en su propio PDF. */}
      {informes.length > 0 && !verInforme && (
        <p className="fichas-pdf">
          {txt.abrirInforme}{" "}
          {informes.map((sku, i) => (
            <span key={sku}>
              {i > 0 && " · "}
              <a href={`/api/informe/${encodeURIComponent(sku)}`} target="_blank" rel="noopener noreferrer">{nombres[sku] || sku}</a>
            </span>
          ))}
        </p>
      )}

      {tarjeta.cifra && tarjeta.cifra.valor !== null && (() => {
        // La unidad llega como texto o como par `(es, en)`; se resuelve antes de pintarla.
        const unidad = celda(tarjeta.cifra.unidad, idioma);
        return (
          <p className="cifra">
            <strong>{fmtCifra(tarjeta.cifra.valor, unidad)}</strong>
            {unidad !== "€" && <span className="cifra-unidad">{unidad}</span>}
            <span className="cifra-etiqueta">{bi(tarjeta.cifra.etiqueta, idioma)}</span>
            {tarjeta.cifra.banda && (
              <span className="cifra-banda">
                {fmtCifra(tarjeta.cifra.banda[0], unidad)} … {fmtCifra(tarjeta.cifra.banda[1], unidad)}
              </span>
            )}
          </p>
        );
      })()}

      {(hayGrafico || hayTabla) && (
        <div className="vistas">
          <div className="pestanas" role="tablist" aria-label={bi(tarjeta.titulo, idioma)}>
            {hayGrafico && (
              <button role="tab" aria-selected={vista === "grafico"} className={vista === "grafico" ? "sel" : ""}
                      onClick={() => setVista("grafico")}>
                <span aria-hidden="true">◫</span> {txt.grafico}
              </button>
            )}
            {hayTabla && (
              <button role="tab" aria-selected={vista === "tabla"} className={vista === "tabla" ? "sel" : ""}
                      onClick={() => setVista("tabla")}>
                <span aria-hidden="true">▤</span> {txt.tabla}
                <em>{tarjeta.tabla.length}</em>
              </button>
            )}
          </div>

          <div className="vista" role="tabpanel">
            {vista === "grafico" && g && (
              <>
                {g.tipo === "heatmap" && <GraficoHeatmap grafico={g} idioma={idioma} />}
                {g.tipo === "serie" && <GraficoSerie grafico={g} idioma={idioma} />}
                {g.tipo === "dispersion" && <GraficoDispersion grafico={g} idioma={idioma} />}
                {g.tipo === "barras_apiladas" && <GraficoBarrasApiladas grafico={g} idioma={idioma} />}
                {g.tipo === "barras" && <GraficoBarras grafico={g} idioma={idioma} />}
                {g.tipo === "radar" && <GraficoRadar grafico={g} idioma={idioma} />}
              </>
            )}
            {vista === "tabla" && hayTabla && (
              <Tabla filas={tarjeta.tabla} encabezados={tarjeta.encabezados} idioma={idioma} />
            )}
          </div>
        </div>
      )}

      {/* El pie: fuente con su owner, sistema, corte, confianza, filas y la marca de sintético.
          En UNA línea (con el detalle en `title`) porque apilado en cuatro bloques medía cinco
          líneas por tarjeta y con tres tarjetas empujaba la respuesta fuera de la pantalla.
          Ningún dato se pierde: es el mismo que exige `verificar()` en Python. */}
      <footer className="procedencia">
        <span className="proc-fuentes">
          {tarjeta.fuentes.map((f) => (
            <span key={f.fuente_id} className="fuente" title={`${f.sistema_origen} · ${txt.owner}: ${f.owner}`}>
              {bi(f.nombre, idioma)}
              <em> · {f.owner}</em>
              {!f.declarada_en_slide && <span className="chip aviso" title={txt.asumidaLargo}>{txt.asumida}</span>}
            </span>
          ))}
        </span>
        <span className="proc-meta">
          <span title={txt.corte}>{tarjeta.corte.texto}</span>
          <span title={tarjeta.confianza.avisos.map((a) => bi(a, idioma)).join("; ") || txt.confianza}>
            {txt.confianza}: {txt.confianzas[tarjeta.confianza.nivel]}
            {tarjeta.confianza.avisos.length > 0 && <em> ⓘ</em>}
          </span>
          <span>{tarjeta.evidencia.n} {txt.usadas}</span>
          <ChipProcedencia origen={tarjeta.origen} idioma={idioma} />
        </span>
      </footer>
    </section>
  );
}
